import React, { useMemo, Component } from 'react';
import { Package, Star, ClipboardList, MapPin, Calendar, Clock, LogOut, ThumbsUp, ThumbsDown, Megaphone } from 'lucide-react';
import { Button } from './ui/Button';
import type { AdminCatch, AdminReview, AdminUser } from '../lib/supabase';
import type { Event } from '../lib/store';
interface BrandDashboardProps {
  brand: string;
  events: Event[];
  catches: AdminCatch[];
  reviews: AdminReview[];
  users: AdminUser[];
  questionLabelMap: Record<string, string>;
  onLogout: () => void;
}
const norm = (s: string) => (s || '').trim().toLowerCase();
/**
 * BRAND-ONLY DATA VIEW.
 *
 * A logged-in brand sees ONLY their own slice of the data — nothing else:
 *   1. Products caught (their brand only)
 *   2. Reviewed ratio (of their caught boxes)
 *   3. Exact answers to each question (from people who caught their drops)
 *   4. Where & when each catch happened
 */
export function BrandDashboard({
  brand,
  events,
  catches,
  reviews,
  users,
  questionLabelMap,
  onLogout
}: BrandDashboardProps) {
  const brandKey = norm(brand);
  // Drops belonging to this brand (for location/time + product lists).
  const brandEvents = useMemo(() => events.filter(e => norm(e.brand) === brandKey), [events, brandKey]);
  const brandEventById = useMemo(() => {
    const m = new Map<string, Event>();
    brandEvents.forEach(e => m.set(e.id, e));
    return m;
  }, [brandEvents]);
  // Catches of this brand's drops (match on the catch's stored brand).
  const brandCatches = useMemo(() => catches.filter(c => norm(c.eventBrand) === brandKey), [catches, brandKey]);
  // 1) Products caught — total + per-product (from each drop's products list).
  const productCounts = useMemo(() => {
    const counts: Record<string, number> = {};
    brandEvents.forEach(e => (e.products || []).forEach(p => {
      if (norm(p.brand) === brandKey || (e.products || []).length) {
        counts[p.name] = counts[p.name] || 0;
      }
    }));
    // Count one "product caught" per catch × products in that drop.
    brandCatches.forEach(c => {
      const ev = events.find(e => e.title === c.eventTitle && norm(e.brand) === brandKey);
      const ps = ev?.products?.filter(p => norm(p.brand) === brandKey) || [];
      if (ps.length === 0) {
        counts[c.eventTitle || 'Catch'] = (counts[c.eventTitle || 'Catch'] || 0) + 1;
      } else {
        ps.forEach(p => {
          counts[p.name] = (counts[p.name] || 0) + 1;
        });
      }
    });
    return Object.entries(counts).sort((a, b) => b[1] - a[1]);
  }, [brandEvents, brandCatches, events, brandKey]);
  const totalCaught = brandCatches.length;
  // 2) Reviewed ratio — caught boxes that reached "reviewed" status.
  const reviewedCount = useMemo(() => brandCatches.filter(c => norm(c.status) === 'reviewed').length, [brandCatches]);
  const reviewedRatio = totalCaught > 0 ? Math.round(reviewedCount / totalCaught * 100) : 0;
  // Per-product review sentiment for THIS brand only.
  const brandReviews = useMemo(() => reviews.filter(r => norm(r.productBrand) === brandKey), [reviews, brandKey]);
  const reviewSentiment = useMemo(() => {
    const yes = brandReviews.filter(r => r.buyAgain === true).length;
    const total = brandReviews.filter(r => r.buyAgain !== null).length;
    return {
      yes,
      total
    };
  }, [brandReviews]);
  // 3) Exact answers per question — only from people who caught this brand.
  const brandUserPhones = useMemo(() => new Set(brandCatches.map(c => c.userId)), [brandCatches]);
  const answersByQuestion = useMemo(() => {
    const map: Record<string, string[]> = {};
    users.filter(u => brandUserPhones.has(u.phone)).forEach(u => {
      Object.entries(u.customAnswers || {}).forEach(([qid, answer]) => {
        if (!answer) return;
        const label = questionLabelMap[qid] || qid;
        if (!map[label]) map[label] = [];
        map[label].push(answer);
      });
    });
    return Object.entries(map);
  }, [users, brandUserPhones, questionLabelMap]);
  // 4) Where & when — each catch's drop location + date/time.
  const whereWhen = useMemo(() => {
    return brandCatches.map(c => {
      const ev = events.find(e => e.title === c.eventTitle && norm(e.brand) === brandKey) || null;
      return {
        id: c.id,
        title: c.eventTitle || 'Catch',
        address: ev?.address || '—',
        date: ev?.date || '',
        time: ev?.time || '',
        when: c.timestamp,
        status: c.status
      };
    }).sort((a, b) => b.when - a.when);
  }, [brandCatches, events, brandKey]);
  // Campaigns belonging to this brand, with each campaign's drop count and the
  // number of catches across its drops. Scoped entirely to this brand.
  const campaigns = useMemo(() => {
    const map = new Map<string, {
      name: string;
      drops: number;
      caught: number;
    }>();
    brandEvents.forEach(e => {
      const name = e.campaign || e.title || 'Untitled campaign';
      const entry = map.get(name) || {
        name,
        drops: 0,
        caught: 0
      };
      entry.drops += 1;
      entry.caught += brandCatches.filter(c => {
        const ev = events.find(x => x.title === c.eventTitle && norm(x.brand) === brandKey);
        return ev?.id === e.id;
      }).length;
      map.set(name, entry);
    });
    return Array.from(map.values()).sort((a, b) => b.caught - a.caught);
  }, [brandEvents, brandCatches, events, brandKey]);
  const fmt = (ms: number) => ms ? new Date(ms).toLocaleString() : '—';
  return <div className="min-h-screen bg-gray-950 text-white" data-id="element-185" data-mp="cho4408hbi4">
      {/* Header */}
      <header className="bg-gray-900 border-b border-gray-800 px-6 py-4 flex items-center justify-between sticky top-0 z-10" data-id="element-186" data-mp="265g2e4fj1fjm">
        <div className="flex items-center space-x-3" data-id="element-187" data-mp="2255wssvncr5a">
          <div className="bg-white rounded-lg px-2.5 py-1 flex items-center" data-id="element-188" data-mp="538dbpernvbn">
            <img src="https://cdn.magicpatterns.com/uploads/bcWTzmedNUNKBwzbD46o8N/2.png" alt="GONE FISHING" className="h-7 object-contain" data-id="element-189" data-mp="a1a1upuyomui" />
          </div>
          <div data-id="element-190" data-mp="jzzikfb4g196">
            <span className="text-xs bg-brand-yellow/20 text-brand-yellow px-2 py-0.5 rounded font-mono" data-id="element-191" data-mp="k0r5z2ukesod">
              BRAND
            </span>
            <h1 className="text-lg font-bold leading-tight" data-id="element-192" data-mp="nzgnn7mmywdt">{brand}</h1>
          </div>
        </div>
        <button onClick={onLogout} className="flex items-center text-xs bg-gray-800 hover:bg-gray-700 text-gray-300 px-3 py-1.5 rounded-lg transition-colors" data-id="element-193" data-mp="sy1g2kvncbrp">
          <LogOut className="w-3.5 h-3.5 mr-1.5" data-id="element-194" data-mp="12sa2qrxzxahy" />
          Log out
        </button>
      </header>

      <div className="max-w-4xl mx-auto p-4 sm:p-6 space-y-6" data-id="element-195" data-mp="17n7dc3axbrwy">
        {/* Summary cards */}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4" data-id="element-196" data-mp="1bosva32bewe0">
          <SummaryCard icon={Package} label="Products caught" value={String(totalCaught)} sub={`${brandEvents.length} drop${brandEvents.length === 1 ? '' : 's'}`} data-id="element-197" data-mp="2w9z9nc89wx0g" />
          <SummaryCard icon={Star} label="Reviewed ratio" value={`${reviewedRatio}%`} sub={`${reviewedCount} of ${totalCaught} reviewed`} data-id="element-198" data-mp="2n8hzsblglrk7" />
          <SummaryCard icon={ThumbsUp} label="Would buy again" value={reviewSentiment.total > 0 ? `${Math.round(reviewSentiment.yes / reviewSentiment.total * 100)}%` : '—'} sub={`${reviewSentiment.yes} of ${reviewSentiment.total} reviews`} data-id="element-199" data-mp="3fw41tff1go4y" />
        </div>

        {/* Your campaigns — scoped to this brand only */}
        <section className="bg-gray-900 rounded-2xl p-6 border border-gray-800" data-id="element-200" data-mp="3twom6g0lqs0k">
          <h2 className="text-lg font-bold mb-4 flex items-center" data-id="element-201" data-mp="3tulorax4dwlt">
            <Megaphone className="w-5 h-5 mr-2 text-brand-yellow" data-id="element-202" data-mp="328bv3wffm3k8" />
            Your Campaigns
          </h2>
          {campaigns.length === 0 ? <p className="text-gray-500 text-sm" data-id="element-203" data-mp="2t8f7ly4s3aqr">
              No campaigns yet for {brand}.
            </p> : <div className="space-y-2" data-id="element-204" data-mp="2x990l5x2vfqi">
              {campaigns.map(c => <div key={c.name} className="flex items-center justify-between bg-gray-950 rounded-lg px-4 py-3 border border-gray-800" data-id="element-205" data-mp="3kzuxc11mtdl7">
                  <div data-id="element-206" data-mp="3bz6rvuqb0ljx">
                    <span className="text-gray-200 text-sm font-medium" data-id="element-207" data-mp="3bzpxqd2ojjhl">
                      {c.name}
                    </span>
                    <span className="text-gray-500 text-xs ml-2" data-id="element-208" data-mp="3gwggj85vct6p">
                      {c.drops} drop{c.drops === 1 ? '' : 's'}
                    </span>
                  </div>
                  <span className="font-bold text-brand-yellow text-sm" data-id="element-209" data-mp="25jw55z8ets6l">
                    {c.caught} caught
                  </span>
                </div>)}
            </div>}
        </section>

        {/* 1) Products caught */}
        <section className="bg-gray-900 rounded-2xl p-6 border border-gray-800" data-id="element-210" data-mp="27002iqwn83yk">
          <h2 className="text-lg font-bold mb-4 flex items-center" data-id="element-211" data-mp="26xxdoa788gvf">
            <Package className="w-5 h-5 mr-2 text-brand-yellow" data-id="element-212" data-mp="2228c61bncpwh" />
            Products Caught
          </h2>
          {productCounts.length === 0 ? <p className="text-gray-500 text-sm" data-id="element-213" data-mp="19c9g3lz1bsib">No catches yet.</p> : <div className="space-y-2" data-id="element-214" data-mp="19aexwff5eehx">
              {productCounts.map(([name, count]) => <div key={name} className="flex items-center justify-between bg-gray-950 rounded-lg px-4 py-3 border border-gray-800" data-id="element-215" data-mp="1o2yxj633a3um">
                  <span className="text-gray-200 text-sm" data-id="element-216" data-mp="vis02b5k4xx1">{name}</span>
                  <span className="font-bold text-brand-yellow" data-id="element-217" data-mp="qkyw7orq8fzx">{count}</span>
                </div>)}
            </div>}
        </section>

        {/* 3) Exact answers per question */}
        <section className="bg-gray-900 rounded-2xl p-6 border border-gray-800" data-id="element-218" data-mp="256n8dnhuiq6a">
          <h2 className="text-lg font-bold mb-4 flex items-center" data-id="element-219" data-mp="205zjp8h3alf8">
            <ClipboardList className="w-5 h-5 mr-2 text-brand-yellow" data-id="element-220" data-mp="1v86byb051jcu" />
            Answers by Question
          </h2>
          {answersByQuestion.length === 0 ? <p className="text-gray-500 text-sm" data-id="element-221" data-mp="1r8fexpk0q6zv">
              No question answers from your catchers yet.
            </p> : <div className="space-y-5" data-id="element-222" data-mp="1hfzwkr4t7geg">
              {answersByQuestion.map(([question, answers]) => {
            // Tally identical answers so brands see the distribution.
            const tally: Record<string, number> = {};
            answers.forEach(a => {
              tally[a] = (tally[a] || 0) + 1;
            });
            const rows = Object.entries(tally).sort((a, b) => b[1] - a[1]);
            return <div key={question} data-id="element-223" data-mp="3f3tx2xpi96zn">
                    <h3 className="text-sm font-bold text-gray-300 mb-2" data-id="element-224" data-mp="3j5fbwollnvch">
                      {question}
                    </h3>
                    <div className="space-y-1.5" data-id="element-225" data-mp="3o63apwg2lgh9">
                      {rows.map(([answer, count]) => <div key={answer} className="flex items-center justify-between bg-gray-950 rounded-lg px-3 py-2 border border-gray-800 text-sm" data-id="element-226" data-mp="3t3cv2xolr1y8">
                          <span className="text-gray-200" data-id="element-227" data-mp="30cuuc3bcpcnz">{answer}</span>
                          <span className="text-gray-400 font-mono" data-id="element-228" data-mp="35cpocfqfaid9">
                            ×{count}
                          </span>
                        </div>)}
                    </div>
                  </div>;
          })}
            </div>}
        </section>

        {/* 4) Where & when */}
        <section className="bg-gray-900 rounded-2xl p-6 border border-gray-800" data-id="element-229" data-mp="29nrx14dgocmu">
          <h2 className="text-lg font-bold mb-4 flex items-center" data-id="element-230" data-mp="25metj0xuclj3">
            <MapPin className="w-5 h-5 mr-2 text-brand-yellow" data-id="element-231" data-mp="25m6f34m2l85s" />
            Where &amp; When Caught
          </h2>
          {whereWhen.length === 0 ? <p className="text-gray-500 text-sm" data-id="element-232" data-mp="2ee8fx401wqgl">No catches recorded yet.</p> : <div className="space-y-3" data-id="element-233" data-mp="1qomesb04c15y">
              {whereWhen.map(w => <div key={w.id} className="bg-gray-950 rounded-lg p-4 border border-gray-800" data-id="element-234" data-mp="1mnkerrydnl2x">
                  <div className="flex items-center justify-between mb-2" data-id="element-235" data-mp="20o4ycqc49kxh">
                    <span className="font-bold text-white text-sm" data-id="element-236" data-mp="1vnzdbt0cqwod">
                      {w.title}
                    </span>
                    <span className={`text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded ${norm(w.status) === 'reviewed' ? 'bg-green-500/20 text-green-400' : 'bg-yellow-500/20 text-yellow-400'}`} data-id="element-237" data-mp="3c17gvqxs0kka">
                      {w.status}
                    </span>
                  </div>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 text-xs text-gray-400" data-id="element-238" data-mp="xsda7zelrl08">
                    <span className="flex items-center" data-id="element-239" data-mp="jxb5vgpem3pj">
                      <MapPin className="w-3.5 h-3.5 mr-1.5 text-gray-600 shrink-0" data-id="element-240" data-mp="ouuk4gm3qghh" />
                      {w.address}
                    </span>
                    <span className="flex items-center" data-id="element-241" data-mp="1gl7o0q0hp72v">
                      <Calendar className="w-3.5 h-3.5 mr-1.5 text-gray-600 shrink-0" data-id="element-242" data-mp="1gkpj74zrhkjc" />
                      {w.date || '—'} {w.time}
                    </span>
                    <span className="flex items-center sm:col-span-2" data-id="element-243" data-mp="17sdt5k8k7z3w">
                      <Clock className="w-3.5 h-3.5 mr-1.5 text-gray-600 shrink-0" data-id="element-244" data-mp="17qkmi4xy5evw" />
                      Caught {fmt(w.when)}
                    </span>
                  </div>
                </div>)}
            </div>}
        </section>
      </div>
    </div>;
}
function SummaryCard({
  icon: Icon,
  label,
  value,
  sub
}: {
  icon: ComponentType<{
    className?: string;
  }>;
  label: string;
  value: string;
  sub: string;
}) {
  return <div className="bg-gray-900 rounded-2xl p-5 border border-gray-800" data-id="element-245" data-mp="2n8uo7rmappw">
      <div className="flex items-center text-gray-400 text-xs font-bold uppercase tracking-wider mb-2" data-id="element-246" data-mp="3tts0t9midrhv">
        <Icon className="w-4 h-4 mr-2 text-brand-yellow" data-id="element-247" data-mp="uctusj298c9x" />
        {label}
      </div>
      <div className="text-3xl font-bold text-white" data-id="element-248" data-mp="qc1iti1zzt9y">{value}</div>
      <div className="text-xs text-gray-500 mt-1" data-id="element-249" data-mp="qent3pg9wmvv">{sub}</div>
    </div>;
}